import _ from "lodash";
import { STRING } from "../constant";
import { GET_LOCAL_QUESTIONS } from "./content";
import {
  DIAGNOSTIC_TEST_QUESTION_COUNT,
  DIAGNOSTIC_TEST_SECONDS_PER_QUESTION,
  pickDiagnosticQuestions,
} from "@learnzapp/core/readiness";
import { computeQuestionPools } from "../../lzapps_core_code/business/authProvider/questionPools";
import { restrictToFreeSet, applyFreeSetToPools } from "@learnzapp/core/study";

export function GET_CUSTOM_TEST_DETAILS(
  questions,
  timePerSecond,
  testDetails,
  profile,
  questionScore
) {
  if (!testDetails) return null;

  // Bundle-anchored, type-insensitive classification — same shared helper the dashboard uses, so the
  // custom test and the readiness numbers agree. Fixes the strict `.includes()` that dropped
  // string-typed topic_id records and the stub records (times_answered === 0) that leaked in.
  const pools = computeQuestionPools(
    questions,
    questionScore?.all_questions_scores ?? [],
    testDetails.topic_ids ?? [],
    { bookmarkIds: profile?.question_bookmarks ?? [] }
  );
  let incorrectQuestions = pools.incorrect;
  let bookmarkedQuestions = pools.bookmarked;
  let unansweredQuestion = pools.unanswered;

  // Answered score records (real answers only: times_answered >= 1, in-bundle) used to top up
  // DEFAULT / incorrect / bookmark tests. Kept in record shape so the padding code below is unchanged.
  const answeredIdSet = new Set(pools.attempted.map((q) => String(q.id)));
  let attemptQuestions = (questionScore?.all_questions_scores ?? []).filter(
    (e) => (e?.times_answered ?? 0) >= 1 && answeredIdSet.has(String(e.question_id))
  );

  // Bounded free set (FREE_SET_ENFORCED): EVERY pool — unanswered, attempted,
  // incorrect and bookmarked — is restricted to the free set (core
  // applyFreeSetToPools). The former incorrect/bookmarked exemption ("Amendment
  // 4") was retired by the lapsed-subscriber policy (#63).
  if (Array.isArray(testDetails.free_set_ids)) {
    const restricted = applyFreeSetToPools(
      { unanswered: unansweredQuestion, incorrect: incorrectQuestions, bookmarked: bookmarkedQuestions },
      testDetails.free_set_ids
    );
    unansweredQuestion = restricted.unanswered;
    incorrectQuestions = restricted.incorrect;
    bookmarkedQuestions = restricted.bookmarked;
    attemptQuestions = restrictToFreeSet(attemptQuestions, testDetails.free_set_ids, (e) => e.question_id);
  }

  if (
    testDetails.priority == STRING.default ||
    testDetails.priority == STRING.unanswered
  ) {
    let randomQuestions = _.shuffle(unansweredQuestion);

    // "Questions I haven't answered yet" is strict — never pad with already-answered questions, so a
    // shorter test is correct. DEFAULT ("smart logic") may top up to reach the count / fill a timer.
    const isStrictUnanswered = testDetails.priority == STRING.unanswered;
    if (!isStrictUnanswered) {
      if (testDetails?.total_time > 0) {
        const attemptQuestionByScore = _.sortBy(attemptQuestions, (e) => e.score);
        const attemptQuestionIdByScore = attemptQuestionByScore.map((e) => {
          return { question_id: e.question_id, topic_id: e.topic_id };
        });
        randomQuestions = randomQuestions.concat(_.shuffle(attemptQuestionIdByScore));
      } else if (unansweredQuestion.length <= testDetails.num_questions) {
        const attemptQuestionByScore = _.sortBy(attemptQuestions, (e) => e.score);
        const attemptQuestionIdByScore = attemptQuestionByScore.map((e) => {
          return { question_id: e.question_id, topic_id: e.topic_id };
        });
        const shuffleAttemptQuestion = _.shuffle(attemptQuestionIdByScore);

        const remainingQuestion =
          testDetails.num_questions - randomQuestions.length;
        const remainingFinalList = _.take(
          shuffleAttemptQuestion,
          remainingQuestion
        );
        randomQuestions = randomQuestions.concat(remainingFinalList);
      }
    }

    const history = randomQuestions.map((obj) => {
      if (obj.id) {
        return {
          question_id: obj.id,
          response: STRING.unanswered_tag,
          selected_choice: "X",
          topic_id: obj.topic_id,
        };
      } else if (obj.question_id) {
        return {
          question_id: obj.question_id,
          response: STRING.unanswered_tag,
          selected_choice: "X",
          topic_id: obj.topic_id,
        };
      }
    });

    let array = [];
    let time = 0;

    if (testDetails.num_questions > 0) {
      array = _.take(history, testDetails.num_questions);
      time = array.length * timePerSecond;
    } else {
      const totalQuestions = Math.max(
        1,
        Math.floor((testDetails.total_time * 60) / (timePerSecond || 90))
      );
      array = _.take(history, totalQuestions);
      time = testDetails.total_time * 60;
    }

    if (testDetails.is_untimed) {
      time = 0;
    }

    const redux = {
      test_name: testDetails.test_name,
      test_questions_meta_data: array,
      total: array.length,
      total_time: time,
      remaining_time: time,
      time_taken: 0,
    };

    return redux;
  } else if (testDetails.priority == STRING.bookmark) {
    // Strict — only bookmarked questions; never pad with other questions.
    let randomQuestions = _.shuffle(bookmarkedQuestions);

    const history = randomQuestions.map((obj) => {
      if (obj.id) {
        return {
          question_id: obj.id,
          response: STRING.unanswered_tag,
          selected_choice: "X",
          topic_id: obj.topic_id,
        };
      } else if (obj.question_id) {
        return {
          question_id: obj.question_id,
          response: STRING.unanswered_tag,
          selected_choice: "X",
          topic_id: obj.topic_id,
        };
      }
    });

    let array = [];
    let time = 0;

    if (testDetails.num_questions > 0) {
      array = _.take(history, testDetails.num_questions);
      time = array.length * timePerSecond;
    } else {
      const totalQuestions = Math.max(
        1,
        Math.floor((testDetails.total_time * 60) / (timePerSecond || 90))
      );
      array = _.take(history, totalQuestions);
      time = testDetails.total_time * 60;
    }

    if (testDetails.is_untimed) {
      time = 0;
    }

    const redux = {
      test_name: testDetails.test_name,
      test_questions_meta_data: array,
      total: array.length,
      total_time: time,
      remaining_time: time,
      time_taken: 0,
    };

    return redux;
  } else if (testDetails.priority == STRING.incorrect_tag) {
    // Strict — only incorrectly-answered questions; never pad with other questions.
    let randomQuestions = _.shuffle(incorrectQuestions);

    const history = randomQuestions.map((obj) => {
      if (obj.id) {
        return {
          question_id: obj.id,
          response: STRING.unanswered_tag,
          selected_choice: "X",
          topic_id: obj.topic_id,
        };
      } else if (obj.question_id) {
        return {
          question_id: obj.question_id,
          response: STRING.unanswered_tag,
          selected_choice: "X",
          topic_id: obj.topic_id,
        };
      }
    });

    let array = [];
    let time = 0;

    if (testDetails.num_questions > 0) {
      array = _.take(history, testDetails.num_questions);
      time = array.length * timePerSecond;
    } else {
      const totalQuestions = Math.max(
        1,
        Math.floor((testDetails.total_time * 60) / (timePerSecond || 90))
      );
      array = _.take(history, totalQuestions);
      time = testDetails.total_time * 60;
    }

    if (testDetails.is_untimed) {
      time = 0;
    }

    const redux = {
      test_name: testDetails.test_name,
      test_questions_meta_data: array,
      total: array.length,
      total_time: time,
      remaining_time: time,
      time_taken: 0,
    };

    return redux;
  }
}

export function GET_RETAKE_TEST_DETAILS(testDetails) {
  if (!testDetails) return null;
  const history = testDetails?.test_questions_meta_data?.map((obj) => {
    return {
      question_id: obj.question_id,
      response: STRING.unanswered_tag,
      selected_choice: "X",
      topic_id: obj.topic_id,
    };
  });

  const array = _.take(history, testDetails.num_questions);

  const redux = {
    test_name: testDetails.test_name,
    test_questions_meta_data: array,
    total: array.length,
    total_time: array.length * 60,
    remaining_time: array.length * 60,
    time_taken: 0,
  };

  return redux;
}

// Canonical wrapped question lookup now lives in @learnzapp/core/study
// (resolves the questionHelpers name drift; identical {question, choices} shape).
// Re-exported under the existing name so the 11 web call sites are unchanged.
export { getQuestionFromId as GET_QUESTION_FROM_ID } from '@learnzapp/core/study';

export function GET_ONBOARDING_TEST_DETAILS(exam) {
  const questions = GET_LOCAL_QUESTIONS(exam);

  const filterQuestions = questions?.filter(
    (item) => item?.practice_test_id == 1
  );
  // Sizing + domain-stratified sampling shared with mobile via @learnzapp/core
  // so the free assessment can't drift between apps (was a hardcoded 20/90 here
  // while mobile advertised 10 questions · ~10 minutes).
  const randomQuestions = pickDiagnosticQuestions(
    filterQuestions,
    DIAGNOSTIC_TEST_QUESTION_COUNT
  );

  const history = randomQuestions.map((obj) => {

    var choices = [];
    if (obj) {
      choices = [
        obj?.choice_a ?? '',
        obj?.choice_b ?? '',
        obj?.choice_c ?? '',
        obj?.choice_d ?? '',
        obj?.choice_e ?? '',
        obj?.choice_f ?? '',
        obj?.choice_g ?? '',
        obj?.choice_h ?? '',
        obj?.choice_i ?? '',
        obj?.choice_j ?? '',
        obj?.choice_k ?? '',
        obj?.choice_l ?? '',
        obj?.choice_m ?? '',
        obj?.choice_n ?? '',
        obj?.choice_o ?? '',
      ].filter(choice => choice !== null && choice !== '');
    }
    return {
      question_id: obj.id,
      question: obj.question,
      choices: choices,
      correct_answer: obj.correct_answer,
      explanation: obj.explanation,
      response: STRING.unanswered_tag,
      selected_choice: "X",
      topic_id: obj.topic_id,
    };
  });

  const redux = {
    test_questions_meta_data: history,
    total: history.length,
    total_time: history.length * DIAGNOSTIC_TEST_SECONDS_PER_QUESTION,
    remaining_time: history.length * DIAGNOSTIC_TEST_SECONDS_PER_QUESTION,
    time_taken: 0,
  };

  return redux;
}

export function GET_PRACTICE_TEST_DETAILS(
  questions,
  timePerSecond,
  testDetails,
  numberOfQuestion
) {
  if (!testDetails) return null;
  const filterQuestions = questions?.filter(
    (item) => item?.practice_test_id == testDetails?.practice_test_id
  );
  const randomQuestions = _.shuffle(filterQuestions);

  const history = randomQuestions.map((obj) => {
    return {
      question_id: obj.id,
      response: STRING.unanswered_tag,
      selected_choice: "X",
      topic_id: obj.topic_id,
    };
  });

  if (numberOfQuestion) {
    const array = _.take(history, numberOfQuestion);

    const redux = {
      test_name: testDetails.test_name,
      test_questions_meta_data: array,
      total: array.length,
      total_time: array.length * timePerSecond,
      remaining_time: array.length * timePerSecond,
      time_taken: 0,
    };

    return redux;
  }

  const redux = {
    test_name: testDetails.test_name,
    test_questions_meta_data: history,
    total: history.length,
    total_time: history.length * timePerSecond,
    remaining_time: history.length * timePerSecond,
    time_taken: 0,
  };

  return redux;
}
